/**
 * 路由守卫（任务 6d；6e-2 加首登设密码强制）：鉴权态 × 页面栈顶 → 是否强制跳转。
 *
 * 口径（authState.ts / PROGRESS 6c 日志定稿）：
 * - auth=unknown **不跳转**——首帧凭证还在 Worker 的 IndexedDB 里没回包，
 *   跳了会让二次启动闪一下登录页。
 * - logged-out 且不在登录页 → replace 登录页（replace 不是 push：返回键
 *   不应回到受保护页）。跳转前的栈顶记为 returnTo，登录成功回原页。
 * - logged-in 且 needsPassword（首登未设密码，标记持久化在凭证记录里——
 *   刷新/冷启动绕不过）→ 强制 replace 设密码页；在受保护页被强制走时同样
 *   记 returnTo（设完密码回原页）。
 * - 安装引导页（/install，16b）任何鉴权态都放行：它是登录之前的入口页。
 * - logged-in 且 !needsPassword 在登录页/设密码页 → replace 回 returnTo
 *   （刷新丢失则回会话列表）。登录页/设密码页组件自己不做导航，守卫接管。
 *
 * 决策是纯函数（guardAction），接线（connectAuthGuard）订阅 store 并调 router，
 * 两层分开便于单测。
 */
import { topRoute } from './projection';
import { HOME_CHATS, type AppRoute } from './routes';
import type { Router } from './router';
import { shallowEqual } from '~/store/store';
import type { GlobalStore } from '~/store/global';
import type { AuthSlice } from '~/store/authState';

export type GuardAction = 'none' | 'to-login' | 'to-set-password' | 'leave-login';

export interface GuardAuth {
  readonly status: AuthSlice['status'];
  /** 仅 logged-in 时有意义，其余态传 false */
  readonly needsPassword: boolean;
}

/** 登录流程自身的页面（守卫跳转的目的地，不记为 returnTo） */
function isAuthFlowRoute(top: AppRoute): boolean {
  return top.kind === 'login' || top.kind === 'set-password';
}

export function guardAction(auth: GuardAuth, top: AppRoute): GuardAction {
  // 安装引导页（16b）对任何鉴权态放行：iOS 用户必须先在 Safari 里装描述文件
  // 才能开始用，这一步在登录之前，弹登录页等于把人挡在门外。
  if (top.kind === 'install') return 'none';
  if (auth.status === 'unknown') return 'none';
  if (auth.status === 'logged-out') return top.kind === 'login' ? 'none' : 'to-login';
  if (auth.needsPassword) return top.kind === 'set-password' ? 'none' : 'to-set-password';
  return isAuthFlowRoute(top) ? 'leave-login' : 'none';
}

function guardAuthOf(auth: AuthSlice): GuardAuth {
  return {
    status: auth.status,
    needsPassword: auth.status === 'logged-in' && auth.needsPassword,
  };
}

export function connectAuthGuard(store: GlobalStore, router: Router): () => void {
  let returnTo: AppRoute | undefined;

  function apply(): void {
    const state = store.get();
    const top = topRoute(state.nav);
    switch (guardAction(guardAuthOf(state.auth), top)) {
      case 'to-login':
        // 添加账号页（14.34b-2）不记 returnTo：登出态深链过来，登录成功回它
        // 语义怪异（已登录还停在「添加账号」），回会话列表即可
        returnTo = top.kind === 'add-account' ? undefined : top;
        router.replace({ kind: 'login' });
        break;
      case 'to-set-password':
        // 从登录页被强制走时保留此前记下的 returnTo（登录→设密码→回原页一条链）
        if (!isAuthFlowRoute(top)) returnTo = top;
        router.replace({ kind: 'set-password' });
        break;
      case 'leave-login': {
        const dest = returnTo ?? HOME_CHATS;
        returnTo = undefined;
        router.replace(dest);
        break;
      }
      case 'none':
        break;
    }
  }

  // 决策只依赖 (authStatus, needsPassword, 栈顶 kind)，其余状态变化不触发重算
  const unsubscribe = store.subscribe(
    (s) => ({ ...guardAuthOf(s.auth), topKind: topRoute(s.nav).kind }),
    apply,
    shallowEqual,
  );
  // 接线时机晚于 auth 回包的情形（测试/热重载）立即对齐一次
  apply();
  return unsubscribe;
}
